import { z } from 'zod';
/**
 * Upload/download bằng presigned URL — docs/01-tech-stack.md §5.2,
 * docs/security.md §7.
 *
 * ⚠️ Không endpoint nào ở đây nhận **nội dung** file. Hệ thống chạy đúng một
 * instance; stream một file 500MB qua Node là chiếm event loop và băng thông
 * của toàn bộ người dùng còn lại. Client PUT/GET thẳng S3, app chỉ cấp URL sau
 * khi đã kiểm quyền.
 */
/**
 * Chỉ những MIME thật sự cần. Danh sách cho phép chứ không phải danh sách cấm:
 * danh sách cấm luôn thiếu một thứ, và thứ thiếu đó là `image/svg+xml` chạy
 * được script.
 */
export const ALLOWED_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'text/csv',
    'text/markdown',
    'application/zip',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Video bằng chứng lỗi — APM-BUGF-a. Chỉ ba định dạng trình duyệt phát được.
    'video/mp4',
    'video/webm',
    'video/quicktime',
    // Tài liệu soạn trong app — APM-PDOC-a: "Tạo mới tài liệu" ở tab Điều lệ lưu
    // thành tệp dự án. Bằng `CHILLDOC_MIME` (chilldoc.ts); `file.spec.ts` canh.
    'application/vnd.chillpm.doc+json',
    // Tài liệu dự án dạng trang HTML — APM-PDOC-html (người dùng yêu cầu 2026-10-14).
    // Chạy được script nên có ba lớp, mỗi lớp một mình đã chặn: tải về luôn ép
    // `attachment` (`StorageService.presignDownload`, docs/security.md §7); proxy
    // `/_s3` thêm `nosniff` + `CSP: sandbox`; khung xem trước dựng trang trong
    // iframe `srcdoc` + `sandbox=""` + CSP `default-src 'none'` (`html-viewer.tsx`),
    // không bao giờ trỏ iframe vào URL của kho. SVG vẫn **không** vào đây: nó được
    // vẽ bằng `<img>`/inline ở nhiều chỗ, HTML thì chỉ qua trình xem cô lập.
    'text/html',
];
export const AllowedMimeType = z.enum(ALLOWED_MIME_TYPES);
/**
 * Đuôi tệp → MIME, **chỉ** dùng khi trình duyệt không nói gì — APM-DOC-a.
 *
 * ## Vì sao cần bảng này
 *
 * `File.type` của trình duyệt lấy từ sổ đăng ký MIME của hệ điều hành, và với
 * `.md` thì phần lớn máy không có đăng ký nào: trình duyệt trả **chuỗi rỗng**.
 * Chuỗi rỗng trượt `AllowedMimeType`, nên `text/markdown` nằm trong danh sách
 * cho phép mà không lần nào tới được đó — người dùng chỉ thấy "Dữ liệu chưa
 * hợp lệ" và không có cách nào đoán ra vì sao.
 *
 * ## Vì sao KHÔNG đè lên giá trị trình duyệt đã nói
 *
 * Giá trị này đi vào `requiredHeaders` của presigned PUT, tức nó nằm trong chữ
 * ký và trở thành content type mà S3 ghi nhận **vĩnh viễn**. Đuôi tệp là thứ
 * người dùng gõ ra, không phải bằng chứng về nội dung: cho nó quyền phủ quyết
 * thông tin thật của hệ điều hành là để `virus.exe` đổi tên thành `ghi-chu.md`
 * tự khai mình là văn bản.
 *
 * Bảng chỉ **chọn một giá trị trong danh sách đã cho phép**, không nới danh
 * sách ấy — kiểu trả về cưỡng chế điều đó.
 */
export const EXTENSION_MIME_TYPES = {
    md: 'text/markdown',
    markdown: 'text/markdown',
    txt: 'text/plain',
    csv: 'text/csv',
    pdf: 'application/pdf',
    zip: 'application/zip',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    chilldoc: 'application/vnd.chillpm.doc+json',
    html: 'text/html',
    htm: 'text/html',
};
/**
 * MIME suy từ đuôi của `filename`, hoặc `null` khi không tra được.
 *
 * `null` là một câu trả lời, không phải một lỗi: người gọi quyết định nói gì
 * với người dùng. Đoán bừa một giá trị "chung chung" ở đây sẽ đẩy một tệp lạ
 * vào kho với nhãn sai, và nhãn ấy không sửa được sau khi object đã tồn tại.
 */
export function mimeTypeForFilename(filename) {
    const dot = filename.lastIndexOf('.');
    // Không có dấu chấm, hoặc tên kiểu `.gitignore` (chấm ở đầu, không phải đuôi).
    if (dot < 1)
        return null;
    return EXTENSION_MIME_TYPES[filename.slice(dot + 1).toLowerCase()] ?? null;
}
/** 500 MB. Trần này chỉ để chặn nhầm lẫn — S3 mới là nơi chịu tải thật. */
export const MAX_FILE_BYTES = 500 * 1024 * 1024;
/** Trần của ảnh / video bằng chứng lỗi — APM-BUGF-a. */
export const BUG_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const BUG_VIDEO_MAX_BYTES = 100 * 1024 * 1024;
/** `deleted` — xoá ở trang Files, APM-FILES-a. Luôn ở **cuối**: MySQL lưu ENUM theo chỉ số. */
export const FileStatus = z.enum(['pending', 'committed', 'orphaned', 'deleted']);
export const PresignUploadInput = z
    .object({
    projectId: z.uuid(),
    /**
     * Tên hiển thị cho người dùng. **Không** dùng làm object key: key luôn có
     * một UUID riêng, nên hai file trùng tên không đè nhau và tên có ký tự lạ
     * không phá cấu trúc key.
     */
    filename: z.string().min(1).max(255),
    mimeType: AllowedMimeType,
    sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
    /** SHA-256 hex thường, client tự tính trước khi upload. */
    checksumSha256: z
        .string()
        .regex(/^[0-9a-f]{64}$/, 'checksum phải là SHA-256 dạng hex thường, 64 ký tự'),
    /**
     * Tệp này là một phiên bản tài liệu (`APM-703`) ⇒ object nằm dưới tiền tố
     * `document/{id}/` thay vì `attachment/`.
     *
     * Tiền tố quyết định vòng đời trên S3: quy tắc lifecycle, quyền theo prefix
     * và cả việc dọn rác về sau đều đọc nó. Xếp một bản hợp đồng vào chỗ của ảnh
     * chụp màn hình là chuyện không sửa được sau khi object đã tồn tại.
     */
    documentId: z.uuid().optional(),
    /**
     * Tệp này là ảnh/video bằng chứng của một lỗi — APM-BUGF-a. Trần riêng, chặt
     * hơn `MAX_FILE_BYTES`: ảnh chụp màn hình ≤ 5 MB, video quay lại ≤ 100 MB
     * (người dùng chốt 2026-09-25), và chỉ nhận ảnh hoặc video.
     */
    /**
     * `document` — APM-PSPACE-i: the first version of a **new** project
     * document (no `documentId` yet, the document is created right after with
     * this `fileId`). It asks `document:write` instead of `task:create`, so a
     * role that may upload documents but not create tasks can still add one.
     * The object key is unchanged (`attachment/`, as before this flag existed).
     */
    purpose: z.enum(['bug_evidence', 'document']).optional(),
})
    .superRefine((input, ctx) => {
    if (input.purpose !== 'bug_evidence')
        return;
    const isImage = input.mimeType.startsWith('image/');
    const isVideo = input.mimeType.startsWith('video/');
    if (!isImage && !isVideo) {
        ctx.addIssue({
            code: 'custom',
            path: ['mimeType'],
            message: 'Bằng chứng lỗi chỉ nhận ảnh hoặc video',
        });
        return;
    }
    const cap = isImage ? BUG_IMAGE_MAX_BYTES : BUG_VIDEO_MAX_BYTES;
    if (input.sizeBytes > cap) {
        ctx.addIssue({
            code: 'custom',
            path: ['sizeBytes'],
            message: isImage ? 'Ảnh tối đa 5 MB' : 'Video tối đa 100 MB',
        });
    }
});
export const PresignedUpload = z.object({
    fileId: z.uuid(),
    /** PUT thẳng vào đây. Hết hạn sau `expiresInSec`. */
    uploadUrl: z.url(),
    objectKey: z.string(),
    expiresInSec: z.number().int().positive(),
    /**
     * Header **bắt buộc** phải gửi kèm PUT. Chúng nằm trong chữ ký, nên thiếu
     * một cái là S3 từ chối — đó chính là điều ta muốn: client không tự đổi được
     * content type sau khi app đã duyệt.
     */
    requiredHeaders: z.record(z.string(), z.string()),
});
export const FileObjectView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    checksumSha256: z.string(),
    status: FileStatus,
    uploadedBy: z.uuid(),
    createdAt: z.iso.datetime(),
});
export const FileDownloadUrl = z.object({
    url: z.url(),
    filename: z.string(),
    expiresInSec: z.number().int().positive(),
});
/**
 * Tính năng file có dùng được không, và **vì sao không** nếu không.
 *
 * Đọc được bởi mọi người dùng đã đăng nhập, không chỉ admin: nút tải lên nằm
 * trên màn hình của người dùng thường, và họ cần biết lý do nó bị khoá. Endpoint
 * này không lộ gì — không bucket, không endpoint, không credential.
 */
export const FileCapabilities = z.object({
    uploadsEnabled: z.boolean(),
    /** Câu hiển thị được cho người dùng khi `uploadsEnabled` là false. */
    disabledReason: z.string().nullable(),
    maxBytes: z.number().int().positive(),
    allowedMimeTypes: z.array(z.string()),
});
// ── Trang Files của dự án — APM-FILES-a ─────────────────────────────────────
/**
 * File nằm ở **mục** nào của dự án — để bảng Files dẫn được người xem về đúng
 * chỗ file được dùng.
 *
 * - `task`: đính kèm một công việc (`file_object.task_id`);
 * - `document`: một phiên bản của tài liệu (`document_version.file_object_id`).
 *   Phiên bản tài liệu là lịch sử **bất biến**, nên file loại này không xoá được
 *   ở trang Files (người dùng chốt 2026-09-25) — `canDelete` luôn `false`;
 * - `none`: tải lên nhưng chưa gắn vào đâu.
 */
export const ProjectFileLocation = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('task'),
        taskId: z.uuid(),
        /** `null` khi task đã bị xoá — file vẫn còn, chỉ là không còn chỗ để dẫn về. */
        taskKey: z.string().nullable(),
        title: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('document'),
        documentId: z.uuid(),
        title: z.string(),
        versionNo: z.number().int().positive(),
        /**
         * Số phiên bản của tài liệu — APM-PDOC-h. Bảng chỉ hiện tệp của bản **mới
         * nhất**, nên đây là dấu duy nhất cho biết tài liệu còn bản cũ.
         */
        versionCount: z.number().int().positive(),
    }),
    /** Ảnh/video bằng chứng của một lỗi ở tab Kiểm thử — APM-BUGF-a. */
    z.object({
        kind: z.literal('bug'),
        bugId: z.uuid(),
        code: z.number().int().positive(),
        title: z.string(),
    }),
    z.object({ kind: z.literal('none') }),
]);
export const ProjectFileRow = z.object({
    id: z.uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /** Chỉ `committed` (dùng được) hoặc `deleted` — upload dở dang không phải file của dự án. */
    status: z.enum(['committed', 'deleted']),
    uploadedAt: z.iso.datetime(),
    uploadedByName: z.string().nullable(),
    deletedAt: z.iso.datetime().nullable(),
    deletedByName: z.string().nullable(),
    location: ProjectFileLocation,
    /**
     * Người đang xem xoá được file này không — PM (`project:update`) hoặc chính
     * người tải lên, và file không phải phiên bản tài liệu. Tính ở server: cùng
     * luật với cửa chặn của `DELETE`, nên nút không bao giờ mời một lượt bấm bị
     * từ chối.
     */
    canDelete: z.boolean(),
});
export const ProjectFilesView = z.object({
    /** Tổng dung lượng file **còn dùng được** — file đã xoá không tính. */
    usedBytes: z.number().int().nonnegative(),
    fileCount: z.number().int().nonnegative(),
    /** Mới nhất trước. Gồm cả file đã xoá, để bảng nói được "từng có". */
    rows: z.array(ProjectFileRow),
});
/**
 * Dung lượng file theo dự án ở trang Tổng quan — APM-FILES-c.
 *
 * `asOf` (luật #7): dung lượng **tại** một mốc — file đã commit trước mốc và
 * chưa bị xoá tới mốc ấy. Dựng lại được từ `committed_at`/`deleted_at` vì hàng
 * của file đã xoá được giữ lại.
 */
export const PortfolioStorage = z.object({
    asOf: z.iso.datetime().nullable(),
    projects: z.array(z.object({
        projectId: z.uuid(),
        key: z.string(),
        name: z.string(),
        usedBytes: z.number().int().nonnegative(),
        fileCount: z.number().int().nonnegative(),
    })),
});
// ── Hạn mức dung lượng theo tổ chức — APM-QUOTA-a ───────────────────────────
/** 1 GB — mặc định của mọi tổ chức, cùng số với `DEFAULT` của cột. */
export const DEFAULT_ORG_STORAGE_QUOTA_BYTES = 1024 ** 3;
/** Dung lượng của tổ chức đang mở, cho quản trị tổ chức — chỉ đọc. */
export const OrgStorageView = z.object({
    /** File dùng được **cộng** lượt tải lên đang dở — đúng con số cửa chặn đang đếm. */
    usedBytes: z.number().int().nonnegative(),
    quotaBytes: z.number().int().positive(),
    projects: z.array(z.object({
        projectId: z.uuid(),
        key: z.string(),
        name: z.string(),
        usedBytes: z.number().int().nonnegative(),
        fileCount: z.number().int().nonnegative(),
    })),
});
/** Mọi tổ chức và hạn mức — cho quản trị hệ thống. */
export const OrgQuotaList = z.object({
    orgs: z.array(z.object({
        orgId: z.uuid(),
        name: z.string(),
        isDemo: z.boolean(),
        usedBytes: z.number().int().nonnegative(),
        quotaBytes: z.number().int().positive(),
    })),
});
/** Trần 10 TB chỉ để chặn gõ nhầm số không; S3 mới là giới hạn thật. */
export const SetOrgStorageQuotaInput = z.object({
    quotaBytes: z
        .number()
        .int()
        .min(1024 ** 2)
        .max(10 * 1024 ** 4),
});
//# sourceMappingURL=file.js.map